<template>
  <section v-if="source" id="elevation-data" scroll-mt-4>
    <h2>Where does this elevation data come from?</h2>
    <p>
      <template v-if="elevationSummary">{{ elevationSummary }}</template>
      This elevation comes from {{ datasetName }}, published by {{ source.producer }}.
      <template v-if="method">It was {{ method.description }}.</template>
      <template v-if="resolution"> Its measurements are spaced {{ resolution }} apart, {{ detailSentence }}</template>
    </p>
    <p>
      Elevations on this site are provided by
      <a href="https://mapterhorn.com/attribution" target="_blank" rel="noopener">Mapterhorn</a>, an open project that
      combines public elevation data from national mapping agencies and the Copernicus program into one worldwide
      dataset, using the most detailed data available for each place.
    </p>
    <h3>Datasets covering this location</h3>
    <ul text-base text-stone-500>
      <li v-for="item in sources" :key="item.source">
        <a :href="item.website" target="_blank" rel="noopener" :title="item.name">{{ item.label }}</a>
        ({{ formatResolution(item.resolution, elevationUnits) }} grid). {{ item.credit.replace(/\.$/, "") }}.
        <a :href="item.licenseUrl" target="_blank" rel="noopener" font-normal underline>{{ item.license }}</a
        >.
      </li>
    </ul>
  </section>
</template>
<script setup>
const props = defineProps({
  // sources covering the location, finest resolution first
  sources: {
    type: Array,
    default: () => [],
  },
  placeName: String,
  // elevation in meters
  altitude: Number,
  elevationUnits: {
    type: Object,
    required: true,
  },
});

const source = computed(() => props.sources?.[0]);
const method = computed(() => ELEVATION_METHODS[source.value?.method]);
const resolution = computed(() => formatResolution(source.value?.resolution, props.elevationUnits));

// generic labels like "Germany elevation model" read better with "the"; proper names like "swissALTI3D" don't
const datasetName = computed(() => {
  const label = source.value?.label;
  return /(model|DEM|DTM)$/i.test(label) ? `the ${label}` : label;
});

// state the elevation in both units, e.g. "Lauterbrunnen is 9,404 feet (2,866 meters) above sea level."
const elevationSummary = computed(() => {
  if (!isNumber(props.altitude)) return null;
  const feet = numberFormatted(props.altitude * ELEVATION_UNITS.ft.multiplier);
  const meters = numberFormatted(props.altitude);
  const subject = props.placeName || "This location";
  const relation = props.altitude < 0 ? "below" : "above";
  const absFeet = feet.replace("-", "");
  const absMeters = meters.replace("-", "");
  return `${subject} is ${absFeet} feet (${absMeters} meters) ${relation} sea level.`;
});

const detailSentence = computed(() => {
  const meters = source.value?.resolution;
  if (meters <= 2) {
    return "which is detailed enough to capture small features like ditches, embankments, and stream banks.";
  }
  if (meters <= 10) {
    return "which captures the shape of hills and valleys but can smooth over small features like ditches and embankments.";
  }
  return `so each reading represents the average height of a ${resolution.value} square and can miss small hills, cliffs, and gullies.`;
});
</script>
